import { memo, useEffect, useState } from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { MATCH_STATUS } from '@zmedia/bm-soccer-ui/constants';
import { ObjectUtils } from '@zmedia/bm-soccer-ui/utils';
import { useAsyncFn } from '@zmedia/bm-soccer-ui/hooks';
import PageService from '@zmedia/bm-soccer-ui/service/PageService';
import { matchLiveSelector } from '@zmedia/bm-soccer-ui/selectors/match';
import { Columns, Element, Tabs } from '@zmedia/bm-soccer-ui/components';
import { RankingSidebar } from '@zmedia/bm-soccer-ui/layout/sidebar/right';
import { MatchDetailHeader } from './header';
import { MatchLineup } from './lineup';
import { ContentSection } from './content';
import { EventSection } from './event';
import { StatsSection } from './stats';
import { TeamForm } from './TeamForm';
import { HeaderMatchScore } from './header/scores';
import { PredictDetail } from './predict';
import Head2Head from './Head2Head';
import styles from './style.module.scss';
import { twMerge } from 'tailwind-merge';
import { isMiniappSelector } from '@zmedia/bm-soccer-ui/selectors/app';

const SECTION_MATCH = {
    MATCH_INFO: 'match-info',
    MATCH_LINEUP: 'match-lineup',
    MATCH_EVENT: 'match-event',
    MATCH_STATS: 'match-stats',
    MATCH_HEAD_TO_HEAD: 'match-head-to-head',
    MATCH_TEAM_FORM: 'match-team-form',
    MATCH_NEWS: 'match-news',
    MATCH_PREDICT: 'match-predict',
};

const SECTION_MATCH_TITLE = {
    [SECTION_MATCH.MATCH_INFO]: 'Thông tin',
    [SECTION_MATCH.MATCH_LINEUP]: 'Đội hình',
    [SECTION_MATCH.MATCH_EVENT]: 'Diễn biến ',
    [SECTION_MATCH.MATCH_STATS]: 'Thống kê',
    [SECTION_MATCH.MATCH_HEAD_TO_HEAD]: 'Đối đầu',
    [SECTION_MATCH.MATCH_TEAM_FORM]: 'Phong độ',
    [SECTION_MATCH.MATCH_NEWS]: 'Tin tức',
    [SECTION_MATCH.MATCH_PREDICT]: 'Dự đoán',
};

export function MatchDetail(props: any) {
    const { content, listProps, otherData = {}, subSidebar = {} } = props;
    const items = useSelector(matchLiveSelector);
    const router = useRouter();
    const id = router.query.id;
    let data = content;

    if (ObjectUtils.isEmpty(data)) {
        const [state, callback] = useAsyncFn(PageService.getMatchDetail);
        useEffect(() => {
            callback(id);
        }, [id]);

        data = state.value?.data || {};
    }

    const isLive =
        items.filter((item) => {
            return (
                data.id === item.id &&
                (item.status.id === MATCH_STATUS.LIVE ||
                    item.status.id === MATCH_STATUS.HT)
            );
        }).length > 0;

    if (ObjectUtils.isEmpty(data)) {
        return null;
    }

    const notStarted =
        data.status.id === MATCH_STATUS.NOT_STARTED ||
        data.status.id === MATCH_STATUS.POSTPONED;

    return (
        <>
            <StickyTab />
            <Columns
                className={styles['column-detail-match']}
                style={
                    {
                        '--home-color': data?.teamColor?.hColor,
                        '--away-color': data?.teamColor?.aColor,
                    } as React.CSSProperties
                }
                multiline={false}
            >
                <Columns.Column type="main">
                    <MatchDetailHeader {...data} isLive={isLive} />
                    {notStarted ? (
                        <>
                            <MatchLineup {...data} {...otherData} />
                            <Columns
                                className={styles['realtime-section']}
                                multiline={false}
                            >
                                <EventSection
                                    {...data}
                                    {...otherData}
                                    isLive={isLive}
                                />
                                <StatsSection
                                    {...data}
                                    {...otherData}
                                    isLive={isLive}
                                />
                            </Columns>
                        </>
                    ) : (
                        <>
                            <Columns
                                className={styles['realtime-section']}
                                multiline={false}
                            >
                                <EventSection
                                    {...data}
                                    {...otherData}
                                    isLive={isLive}
                                />
                                <StatsSection
                                    {...data}
                                    {...otherData}
                                    isLive={isLive}
                                />
                            </Columns>
                            <MatchLineup {...data} {...otherData} />
                        </>
                    )}

                    <Head2Head {...data} />
                    <TeamForm {...data} />
                    <ContentSection
                        {...data}
                        {...otherData}
                        listProps={listProps}
                    />
                </Columns.Column>
                <Columns.Column type="sub">
                    <PredictDetail {...data} {...subSidebar} />
                    <RankingSidebar
                        {...data}
                        {...data.tournament}
                        {...subSidebar}
                    />
                </Columns.Column>
            </Columns>
            <StickyHeader {...data} />
        </>
    );
}

function StickyTab() {
    const [tabs, setTabs] = useState<any>([]);
    const [activeTab, setActiveTab] = useState<string>('match-info');
    const isIframe = useSelector(isMiniappSelector);
  
    useEffect(() => {
        function setTab() {
            const eleTag: any = document.getElementsByTagName('section') || [];
            const eleSection = Array.from(eleTag).filter((item: any) => {
                return item.getAttribute('name');
            });

            const tags: any = [];
            eleSection.forEach((item: any, index: number) => {
                const tagItem = {
                    active: index === 0,
                    id: item.getAttribute('name'),
                    title: SECTION_MATCH_TITLE[item.getAttribute('name')],
                    position:
                        index === 0 ? 0 : item.getBoundingClientRect().top,
                };
                tags.push(tagItem);
            });

            setTabs(tags);
        }
        setTimeout(() => {
            setTab();
        }, 500);

        window.addEventListener('load', setTab);

        return () => {
            window.removeEventListener('load', setTab);
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const activeTab = tabs.filter(
                (tab: any, index: number, array: any) => {
                    return (
                        tab.position < window.pageYOffset + 100 &&
                        window.pageYOffset + 100 < array[index + 1]?.position
                    );
                }
            );
            setActiveTab(activeTab[0]?.id || tabs[tabs.length - 1]?.id);
        };
        document.addEventListener('scroll', handleScroll);

        return () => {
            document.removeEventListener('scroll', handleScroll);
        };
    }, [tabs]);

    useEffect(() => {
        const eleTabList: any = document.getElementsByClassName(
            styles['tabs-list']
        )[0];
        const index = tabs.findIndex((tab: any) => {
            return tab.id === activeTab;
        });

        if (eleTabList) {
            if (index > tabs.length / 2) {
                eleTabList.scrollLeft = 1000;
            } else {
                eleTabList.scrollLeft = 0;
            }
        }
    }, [activeTab]);

    return (
        <Element className={twMerge(styles['tab-mobile'])}>
            <Tabs
                tabs={tabs}
                activeTab={activeTab}
                className={cn({
                    ['!top-0']: isIframe,
                })}
            ></Tabs>
        </Element>
    );
}
function StickyHeader(props: any) {
    const [stickyHeader, setSticky] = useState<boolean>(false);

    const requestAnimationFrame = () => {
        var top = window.pageYOffset || document.documentElement.scrollTop;
        setSticky(top >= 100);
    };

    const onScroll = () => {
        requestAnimationFrame();
    };

    useEffect(() => {
        requestAnimationFrame();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', () => onScroll);
        };
    }, []);

    return (
        <Element
            className={cn(styles['sticky-header'], {
                [styles['is-stick']]: stickyHeader,
            })}
        >
            <HeaderMatchScore {...props} />
        </Element>
    );
}

export default memo(MatchDetail);
